import Vue from "vue";
import SortedTablePlugin from "vue-sorted-table";
import DatePicker from "vue2-datepicker";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { TH_SARABUN_TTF_BASE64 } from "@/assets/fonts/thsarabun.js";

Vue.use(SortedTablePlugin, {
  ascIcon: '<i class="mdi mdi-arrow-down"></i>',
  descIcon: '<i class="mdi mdi-arrow-up"></i>',
});

export default {
  components: {
    DatePicker,
  },
  data: function () {
    return {
      busy: false,
      busyExport: false,
      apiStatus: "กรุณากดปุ่มแสดงข้อมูลเพื่อค้นหาข้อมูล",
      items: [],
      summary: null,
      filterSeller: "",
      filterCustomer: "", // ค้นหาชื่อบริษัทลูกค้า (บางส่วนได้)
      filterLamsam: "", // "" = ทั้งหมด | lamsam_new | lamsam_old | non_lamsam
      lamsamOptions: [
        { value: "", text: "-- ทุกประเภทงาน --" },
        { value: "lamsam_new", text: "ล่ำซำใหม่ (ตั้งแต่ 1 ก.ย. 69)" },
        { value: "lamsam_old", text: "ล่ำซำเก่า (ก่อน 1 ก.ย. 69)" },
        { value: "non_lamsam", text: "ไม่ใช่ล่ำซำ" },
      ],
      filterStart: new Date(moment().startOf("year").format("YYYY-MM-DD")),
      filterEnd: new Date(moment().endOf("year").format("YYYY-MM-DD")),
      filterPaymentMethod: "", // "" = ทั้งหมด | "0" = เงินสด | "1" = เครดิต

      // server-side pagination
      currentPage: 1,
      perPage: 100,
      totalRows: 0,
      lastPage: 1,
      // ตัวเลือกพนักงานขาย — สะสมจากหน้าที่โหลด (กรองจริงทำฝั่ง server)
      sellerOptions: [],
    };
  },
  computed: {
    sellerList() {
      return this.sellerOptions;
    },
    filteredItems() {
      // กรองพนักงานขายย้ายไปฝั่ง server แล้ว (แบ่งหน้า) — ใช้ข้อมูลหน้าปัจจุบันตรง ๆ
      return this.items || [];
    },
    maxPayments() {
      if (!this.filteredItems.length) return 0;
      return Math.max(...this.filteredItems.map((it) => it.payments_arr.length));
    },
    flatRows() {
      const source = this.filteredItems;
      if (source.length === 0) return [];
      const result = [];
      let rowIndex = 0;
      let si = 0;
      let i = 0;
      while (i < source.length) {
        const sellerName = source[i].seller_name;
        // นับ row ทั้งหมดของ seller นี้
        let sellerCount = 0;
        let j = i;
        while (j < source.length && source[j].seller_name === sellerName) {
          sellerCount++;
          j++;
        }
        // วน customer ภายใน seller
        let k = i;
        let isFirstSeller = true;
        while (k < source.length && source[k].seller_name === sellerName) {
          const customerName = source[k].customer_name;
          let customerCount = 0;
          let m = k;
          while (m < source.length && source[m].seller_name === sellerName && source[m].customer_name === customerName) {
            customerCount++;
            m++;
          }
          let isFirstCustomer = true;
          for (let n = k; n < m; n++) {
            rowIndex++;
            result.push({
              ...source[n],
              rowIndex,
              isFirstSeller,
              sellerRowspan: isFirstSeller ? sellerCount : 0,
              sellerName,
              isFirstCustomer,
              customerRowspan: isFirstCustomer ? customerCount : 0,
              customerName,
            });
            isFirstSeller = false;
            isFirstCustomer = false;
          }
          k = m;
        }
        i += sellerCount;
        si++;
      }
      return result;
    },
  },
  methods: {
    // ยุคงานล่ำซำ ตาม backend: แถว id ติดลบ = แถวรวมรายบิลยุคใหม่ (ฐานวางบิล)
    // แถวใบส่งสินค้าที่มี order_total = ของบิลล่ำซำยุคเก่า (คิดหนี้รายใบแบบเดิม โชว์แค่มูลค่า PI กำกับ)
    lamsamType(row) {
      if (row.id < 0) return "ใหม่";
      if (row.order_total != null && row.order_total !== "") return "เก่า";
      return null;
    },
    formatCurrency(val) {
      return parseFloat(val || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      if (isNaN(d.getTime())) return "-";
      return d.toLocaleDateString("th-TH", {
        year: "2-digit",
        month: "short",
        day: "numeric",
      });
    },
    formatAgingDays(val) {
      if (val === null || val === undefined || val === "") return "-";
      return parseInt(val, 10) + " วัน";
    },
    buildParams(page, perPage) {
      return {
        page: page,
        per_page: perPage,
        start_date: this.filterStart ? moment(this.filterStart).format("YYYY-MM-DD") : "",
        end_date: this.filterEnd ? moment(this.filterEnd).format("YYYY-MM-DD") : "",
        payment_method: this.filterPaymentMethod,
        seller: this.filterSeller || "",
        customer: this.filterCustomer || "",
        lamsam_type: this.filterLamsam || "",
      };
    },

    mapRows(rows) {
      return rows.map((it) => ({
        ...it,
        payments_arr: it.payment_list
          ? it.payment_list.split(",").map(Number)
          : [],
      }));
    },

    async onInitData(page = 1) {
      try {
        this.items = [];
        this.busy = true;
        this.apiStatus = "กำลังค้นหาข้อมูล..";

        const res = await this.$axios.get(
          this.$host + "/api/v1/document/receivable-report",
          { params: this.buildParams(page, this.perPage) }
        );

        this.busy = false;
        this.apiStatus = "ไม่พบข้อมูล";
        this.items = this.mapRows(res.data.data);
        this.summary = res.data.summary;

        const pg = res.data.pagination || {};
        this.currentPage = pg.current_page || page;
        this.totalRows = pg.total || this.items.length;
        this.lastPage = pg.last_page || 1;

        // รายชื่อพนักงานขายมาจาก backend (ครบทุกคนในขอบเขตที่กรอง ไม่ใช่แค่หน้าปัจจุบัน)
        // เดิมสะสมจากแถวในหน้าที่โหลดมา → paginate แล้วเห็นชื่อไม่ครบ
        if (Array.isArray(res.data.sellers)) {
          this.sellerOptions = res.data.sellers;
        }
      } catch (error) {
        this.busy = false;
        this.apiStatus = "ไม่พบข้อมูล";
        this.onExceptionHandler((error.response && error.response.data && error.response.data.message) || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      }
    },

    // ดึงข้อมูลครบทุกหน้า (ทีละ 9999) สำหรับ export — ข้อมูลเยอะอาจใช้เวลาหลายนาที
    async fetchAllForExport() {
      const all = [];
      const chunk = 9999;
      let page = 1;
      let last = 1;
      do {
        const res = await this.$axios.get(
          this.$host + "/api/v1/document/receivable-report",
          { params: this.buildParams(page, chunk) }
        );
        all.push(...this.mapRows(res.data.data));
        last = (res.data.pagination && res.data.pagination.last_page) || 1;
        page++;
      } while (page <= last && page <= 10); // เพดาน 10 รอบ (~100k แถว) กันลูปหลุด
      return all;
    },

    addThaiFont(doc) {
      if (!doc || !TH_SARABUN_TTF_BASE64) return;
      doc.addFileToVFS("THSarabun.ttf", TH_SARABUN_TTF_BASE64);
      doc.addFont("THSarabun.ttf", "THSarabun", "normal");
      doc.addFont("THSarabun.ttf", "THSarabun", "bold");
    },

    async exportPDF() {
      if (this.busyExport) return;
      let exportRows = [];
      try {
        this.busyExport = true;
        exportRows = this.totalRows > this.items.length
          ? await this.fetchAllForExport()
          : this.items;
      } catch (e) {
        this.busyExport = false;
        this.onExceptionHandler(e?.response?.data?.message || "ดึงข้อมูลสำหรับ export ไม่สำเร็จ");
        return;
      }
      this.busyExport = false;

      if (!exportRows.length) {
        this.$bvToast.toast("ไม่มีข้อมูลให้ส่งออก", {
          title: "Export PDF",
          variant: "warning",
          solid: true,
        });
        return;
      }

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });
      this.addThaiFont(doc);
      doc.setFont("THSarabun", "normal");

      const margin = { top: 60, right: 36, bottom: 48, left: 36 };
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      const title = "รายงานลูกหนี้";

      const drawHeader = () => {
        doc.setFontSize(18);
        doc.setFont("THSarabun", "bold");
        doc.text(title, margin.left, 40);
      };
      const drawFooter = (n) => {
        doc.setFontSize(11);
        doc.text(`หน้า ${n}`, pageW - margin.right, pageH - 20, {
          align: "right",
        });
      };

      let pageNo = 1;
      drawHeader();
      drawFooter(pageNo);

      const money = (n) => this.formatCurrency(n);

      const head = [
        ["#", "พนักงานขาย", "ลูกค้า", "เลขที่เอกสาร", "ชื่อโครงการ", "วันที่เอกสาร", "อายุคงค้าง", "ยอดใบรับคำสั่งซื้อ", "สร้างบิลแล้ว", "ชำระแล้วรวม", "ยอดค้างชำระ", "0-30 วัน", "30-90 วัน", "90-365 วัน", "หนี้สูญ (>365)"],
      ];

      const col = {
        0: { cellWidth: 24, halign: "right" },
        1: { cellWidth: 70 },
        2: { cellWidth: 95 },
        3: { cellWidth: 80 },
        4: { cellWidth: 80 },
        5: { cellWidth: 55 },
        6: { cellWidth: 45, halign: "right" },
        7: { cellWidth: 60, halign: "right" }, // ยอดใบรับคำสั่งซื้อ (ล่ำซำ)
        8: { cellWidth: 60, halign: "right" }, // สร้างบิลแล้ว (ล่ำซำ)
        9: { cellWidth: 60, halign: "right" }, // ชำระแล้วรวม (ล่ำซำ)
        10: { cellWidth: 65, halign: "right" },
        11: { cellWidth: 60, halign: "right" },
        12: { cellWidth: 60, halign: "right" },
        13: { cellWidth: 60, halign: "right" },
        14: { cellWidth: 60, halign: "right" },
      };

      const body = exportRows.map((it, i) => [
        i + 1,
        it.seller_name || "-",
        it.customer_name || "-",
        (it.document_no || "-") + (this.lamsamType(it) ? ` (ล่ำซำ${this.lamsamType(it)})` : ""),
        it.project_name || "-",
        this.formatDate(it.document_date),
        this.formatAgingDays(it.aging_days),
        it.order_total != null ? money(it.order_total) : "-",
        it.id < 0 ? money(it.delivered_total) : "-",
        it.id < 0 ? money(it.paid) : "-",
        money(it.outstanding),
        money(it.aging_0_30),
        money(it.aging_30_90),
        money(it.aging_90_365),
        money(it.aging_bad_debt),
      ]);

      doc.autoTable({
        startY: margin.top,
        head,
        body,
        margin,
        pageBreak: "auto",
        tableWidth: "wrap",
        styles: {
          font: "THSarabun",
          fontSize: 10,
          cellPadding: 3,
          overflow: "linebreak",
          lineColor: [220, 220, 220],
          lineWidth: 0.2,
          valign: "middle",
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: 20,
          halign: "center",
        },
        columnStyles: col,
        didDrawPage: (data) => {
          if (data.pageNumber > pageNo) {
            pageNo = data.pageNumber;
            drawHeader();
            drawFooter(pageNo);
          }
        },
      });

      const s = this.summary;
      if (s) {
        const agingSummary = s.aging || {};
        doc.autoTable({
          startY: (doc.lastAutoTable ? doc.lastAutoTable.finalY : margin.top) + 6,
          theme: "plain",
          margin,
          styles: { font: "THSarabun", fontSize: 12, cellPadding: 6 },
          body: [
            ["", "", "", "รวมทั้งหมด", "", "", "", money(s.total_outstanding), money(agingSummary.aging_0_30), money(agingSummary.aging_30_90), money(agingSummary.aging_90_365), money(agingSummary.aging_bad_debt)],
          ],
        });
      }

      const fn = `รายงาน_ลูกหนี้_${new Date().toLocaleDateString("th-TH")}.pdf`;
      doc.save(fn);
    },

    async exportExcel() {
      if (this.busyExport) return;
      let exportRows = [];
      try {
        this.busyExport = true;
        exportRows = this.totalRows > this.items.length
          ? await this.fetchAllForExport()
          : this.items;
      } catch (e) {
        this.busyExport = false;
        this.onExceptionHandler(e?.response?.data?.message || "ดึงข้อมูลสำหรับ export ไม่สำเร็จ");
        return;
      }
      this.busyExport = false;

      if (!exportRows.length) {
        this.$bvToast.toast("ไม่มีข้อมูลให้ส่งออก", {
          title: "Export Excel",
          variant: "warning",
          solid: true,
        });
        return;
      }

      const maxP = Math.max(0, ...exportRows.map((it) => it.payments_arr.length));

      const rows = exportRows.map((it, i) => {
        const row = {
          "#": i + 1,
          "พนักงานขาย": it.seller_name || "-",
          "ลูกค้า": it.customer_name || "-",
          "เลขที่เอกสาร": (it.document_no || "-") + (this.lamsamType(it) ? ` (ล่ำซำ${this.lamsamType(it)})` : ""),
          "ใบวางบิล": it.billing_notes || "-",
          "ชื่อโครงการ": it.project_name || "-",
          "วันที่เอกสาร": this.formatDate(it.document_date),
          "อายุคงค้าง (วัน)": it.aging_days != null && it.aging_days !== "" ? +parseInt(it.aging_days, 10) : "",
        };
        for (let p = 0; p < maxP; p++) {
          row[`ชำระครั้งที่ ${p + 1}`] = it.payments_arr[p] != null ? +it.payments_arr[p] : "";
        }
        row["ยอดใบรับคำสั่งซื้อ"] = it.order_total != null ? +parseFloat(it.order_total || 0) : "";
        row["สร้างบิลแล้ว"] = it.id < 0 ? +parseFloat(it.delivered_total || 0) : "";
        row["ชำระแล้วรวม"] = it.id < 0 ? +parseFloat(it.paid || 0) : "";
        row["ยอดค้างชำระ"] = +parseFloat(it.outstanding || 0);
        row["0-30 วัน"] = +parseFloat(it.aging_0_30 || 0);
        row["30-90 วัน"] = +parseFloat(it.aging_30_90 || 0);
        row["90-365 วัน"] = +parseFloat(it.aging_90_365 || 0);
        row["หนี้สูญ (>365 วัน)"] = +parseFloat(it.aging_bad_debt || 0);
        return row;
      });

      const ws = XLSX.utils.json_to_sheet(rows);

      // static cols: #(0) พนักงานขาย(1) ลูกค้า(2) เลขที่(3) ใบวางบิล(4) โครงการ(5) วันที่(6) อายุ(7)
      // dynamic: ชำระครั้งที่ 1..maxP → col 8..8+maxP-1
      // then: ยอดใบรับคำสั่งซื้อ(ล่ำซำ), ชำระแล้วรวม(ล่ำซำ), ยอดค้างชำระ, 0-30, 30-90, 90-365, หนี้สูญ
      const staticCols = [
        { wch: 4 }, { wch: 18 }, { wch: 24 }, { wch: 16 },
        { wch: 18 }, { wch: 24 }, { wch: 14 }, { wch: 12 },
      ];
      const paymentCols = Array.from({ length: maxP }, () => ({ wch: 14 }));
      const trailingCols = [{ wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }];
      ws["!cols"] = [...staticCols, ...paymentCols, ...trailingCols];

      const ref = XLSX.utils.decode_range(ws["!ref"]);
      const firstMoneyCol = 8;             // ชำระครั้งที่ 1
      const outstandingCol = 11 + maxP;    // ยอดค้างชำระ (ถัดจากยอดใบรับคำสั่งซื้อ+สร้างบิลแล้ว+ชำระแล้วรวม)
      const lastMoneyCol   = 11 + maxP + 4; // หนี้สูญ
      for (let R = ref.s.r + 1; R <= ref.e.r; R++) {
        for (let C = firstMoneyCol; C <= lastMoneyCol; C++) {
          const cell = XLSX.utils.encode_cell({ r: R, c: C });
          if (ws[cell] && ws[cell].t === "n") ws[cell].z = "#,##0.00";
        }
      }

      if (this.summary) {
        const totalRow = ref.e.r + 2;
        const agingSummary = this.summary.aging || {};
        ws[XLSX.utils.encode_cell({ r: totalRow, c: 3 })] = { t: "s", v: "รวมทั้งหมด" };
        ws[XLSX.utils.encode_cell({ r: totalRow, c: outstandingCol })] = { t: "n", v: +this.summary.total_outstanding, z: "#,##0.00" };
        ws[XLSX.utils.encode_cell({ r: totalRow, c: outstandingCol + 1 })] = { t: "n", v: +(agingSummary.aging_0_30 || 0), z: "#,##0.00" };
        ws[XLSX.utils.encode_cell({ r: totalRow, c: outstandingCol + 2 })] = { t: "n", v: +(agingSummary.aging_30_90 || 0), z: "#,##0.00" };
        ws[XLSX.utils.encode_cell({ r: totalRow, c: outstandingCol + 3 })] = { t: "n", v: +(agingSummary.aging_90_365 || 0), z: "#,##0.00" };
        ws[XLSX.utils.encode_cell({ r: totalRow, c: outstandingCol + 4 })] = { t: "n", v: +(agingSummary.aging_bad_debt || 0), z: "#,##0.00" };
      }

      ws["!autofilter"] = { ref: ws["!ref"] };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "ลูกหนี้");
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const fn = `รายงาน_ลูกหนี้_${new Date().toLocaleDateString("th-TH")}.xlsx`;
      saveAs(new Blob([wbout], { type: "application/octet-stream" }), fn);
    },
  },
  mounted() {
    this.onInitData();
  },
};
